import localforage from "localforage";

const keys = {
  accessToken: "accessToken",
  refreshToken: "refreshToken",
  surveyStatus: "surveyStatus",
  appointmentReportItem: "appointmentReportItem",
  appointmentReportCounsellorId: "appointmentReportCounsellorId",
  language: "userLang",
  chatRooms: "chatRooms",
  chatRoomsMessages: "chatRoomsMessages",
  appointments: "appointments",
  internalReportItem: "internalReportItem",
};

class LocalStorageService {
  static getAccessToken() {
    const accessToken = localStorage.getItem(keys.accessToken) || "";
    return accessToken;
  }

  static setAccessToken(accessToken) {
    localStorage.setItem(keys.accessToken, accessToken);
  }

  static removeAccessToken() {
    localStorage.removeItem(keys.accessToken);
  }

  static getRefreshToken() {
    const refreshToken = localStorage.getItem(keys.refreshToken) || "";
    return refreshToken;
  }

  static setRefreshToken(refreshToken) {
    localStorage.setItem(keys.refreshToken, refreshToken);
  }

  static removeRefreshToken() {
    localStorage.removeItem(keys.refreshToken);
  }

  static getSurveyStatus() {
    const status = localStorage.getItem(keys.surveyStatus) || null;
    return status;
  }

  static setSurveyStatus(status) {
    localStorage.setItem(keys.surveyStatus, status);
  }

  static removeSurveyStatus() {
    localStorage.removeItem(keys.surveyStatus);
  }

  static getAppointmentReportItem() {
    const storageValue =
      localStorage.getItem(keys.appointmentReportItem) || null;
    if (storageValue) {
      const reportItem = JSON.parse(storageValue);
      if (
        reportItem &&
        typeof reportItem === "object" &&
        !Array.isArray(reportItem)
      ) {
        return reportItem;
      }
    }

    return null;
  }

  static setAppointmentReportItem(reportItem) {
    const serialized = JSON.stringify(reportItem);
    localStorage.setItem(keys.appointmentReportItem, serialized);
  }

  static removeAppointmentReportItem() {
    localStorage.removeItem(keys.appointmentReportItem);
  }

  static getInternalReportItem() {
    const storageValue = localStorage.getItem(keys.internalReportItem) || null;
    if (storageValue) {
      const reportItem = JSON.parse(storageValue);
      if (
        reportItem &&
        typeof reportItem === "object" &&
        !Array.isArray(reportItem)
      ) {
        return reportItem;
      }
    }

    return null;
  }

  static setInternalReportItem(reportItem) {
    const serialized = JSON.stringify(reportItem);
    localStorage.setItem(keys.internalReportItem, serialized);
  }

  static removeInternalReportItem() {
    localStorage.removeItem(keys.internalReportItem);
  }

  static getAppointmentReportCounsellorId() {
    const reportId =
      localStorage.getItem(keys.appointmentReportCounsellorId) || "";
    return reportId;
  }

  static setAppointmentReportCounsellorId(id) {
    localStorage.setItem(keys.appointmentReportCounsellorId, id);
  }

  static removeAppointmentReportCounsellorId() {
    localStorage.removeItem(keys.appointmentReportCounsellorId);
  }

  static setLanguage(language) {
    localStorage.setItem(keys.language, language ?? "sv");
  }

  static getLanguage() {
    return localStorage.getItem(keys.language) || "sv";
  }

  // --- IndexedDB (localForage) for chatRooms and chatRoomsMessages ---
  static async setChatRooms(chatRooms) {
    await localforage.setItem(keys.chatRooms, chatRooms);
  }

  static async getChatRooms() {
    const chatRooms = await localforage.getItem(keys.chatRooms);
    if (Array.isArray(chatRooms)) {
      return chatRooms;
    }
    return null;
  }

  static async removeChatRooms() {
    await localforage.removeItem(keys.chatRooms);
  }

  static async setChatRoomMessages(chatRoomId, messages) {
    let chatRoomsMessages =
      (await localforage.getItem(keys.chatRoomsMessages)) || {};
    if (
      typeof chatRoomsMessages !== "object" ||
      Array.isArray(chatRoomsMessages)
    ) {
      chatRoomsMessages = {};
    }
    chatRoomsMessages[chatRoomId] = messages;
    await localforage.setItem(keys.chatRoomsMessages, chatRoomsMessages);
  }

  static async getChatRoomMessages(chatRoomId) {
    const chatRoomsMessages = await localforage.getItem(keys.chatRoomsMessages);
    if (
      chatRoomsMessages &&
      typeof chatRoomsMessages === "object" &&
      !Array.isArray(chatRoomsMessages)
    ) {
      return chatRoomsMessages[chatRoomId] ?? null;
    }
    return null;
  }

  static async setGlobalChatRoomMessages(globalChatRoomId, messages) {
    let chatRoomsMessages =
      (await localforage.getItem(keys.chatRoomsMessages)) || {};
    if (
      typeof chatRoomsMessages !== "object" ||
      Array.isArray(chatRoomsMessages)
    ) {
      chatRoomsMessages = {};
    }
    chatRoomsMessages[globalChatRoomId] = messages;
    await localforage.setItem(keys.chatRoomsMessages, chatRoomsMessages);
  }

  static async getGlobalChatRoomMessages(globalChatRoomId) {
    const chatRoomsMessages = await localforage.getItem(keys.chatRoomsMessages);
    if (
      chatRoomsMessages &&
      typeof chatRoomsMessages === "object" &&
      !Array.isArray(chatRoomsMessages)
    ) {
      return chatRoomsMessages[globalChatRoomId] ?? null;
    }
    return null;
  }

  static async removeChatRoomsMessages() {
    await localforage.removeItem(keys.chatRoomsMessages);
  }

  static setAppointments(appointments) {
    const serialized = JSON.stringify(appointments);
    localStorage.setItem(keys.appointments, serialized);
  }

  static getAppointments() {
    const storageValue = localStorage.getItem(keys.appointments) || null;
    if (storageValue) {
      const appointments = JSON.parse(storageValue);
      if (Array.isArray(appointments)) {
        return appointments;
      }
    }

    return null;
  }

  static removeAppointments() {
    localStorage.removeItem(keys.appointments);
  }

  static removeAppCache() {
    LocalStorageService.removeChatRoomsMessages();
    LocalStorageService.removeChatRooms();
    LocalStorageService.removeAppointments();
  }
}

export default LocalStorageService;
